import { LoggingService } from "../loggingSvc";
import { FlightingService } from "../flightingSvc";

export type FeatureIdentifier = string;
export type FeatureConfigValue = boolean | FeatureIdentifier;
export type FeatureConfig = { [appId: string]: { [featureId: string]: FeatureConfigValue } };

export class FeatureConfigService {
  private features: FeatureConfig = {};

  // @ngInject
  constructor(private flightingService: FlightingService, private loggingService: LoggingService) {
    //
  }

  /**
   * Checks if a specific feature is enabled.
   *
   * Returns true if flight is enabled, if the feature is set as enabled, or is in the dev environment.
   *
   * Returns false if flight is disabled, if the feature is set as disabled, or if the feature name does not exist.
   *
   * @param featureId {FeatureIdentifier} The name of the feature to check
   */
  public isEnabled(appId: string, featureId: FeatureIdentifier): boolean {
    const feature: FeatureConfigValue = this.features[appId] ? this.features[appId][featureId] : undefined;

    if (typeof feature === "boolean") {
      return feature;
    }

    if (!feature) {
      // log when a feature is not found.
      this.loggingService.logError(
        new Error("Feature not found"),
        "FeatureConfig",
        `Feature ${featureId} was not found`
      );
      return false;
    }

    return this.flightingService.isFlightEnabled(featureId);
  }

  /**
   * Register a feature state as one of the following:
   *   false    - feature is disabled
   *   true     - feature is enabled
   *   "string" - feature is under flighting with "string" being the flight id in Carbon Flighting
   *
   * @param appId {string} Application identifier
   * @param featureId {FeatureIdentifier} Unique feature identifier.
   * @param featureValue {FeatureConfigValue} true, false, or a string representing the flight id in Carbon Flighting.
   */
  public registerFeature(appId: string, featureId: FeatureIdentifier, featureValue: FeatureConfigValue): void {
    if (!this.features[appId]) {
      this.features[appId] = {};
    }
    this.features[appId][featureId] = featureValue;
  }

  /**
   * Gets all registered features.
   */
  public getAllRegisteredFeatures(): FeatureConfig {
    return this.features;
  }
}
